import { memo, useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import { selectRuleConfigBit } from '../../../../../../../store/selectors/ruleCardSelectors'
import { ruleCardActions } from '../../../../../../../store/slices/ruleCardSlice'
import { getEmptyData } from '../../../../../../../utils/InitData'
import { getBlockFooterData } from '../../../../../../../utils/blockFooterData'
import { RuleWrappedBlock } from './RuleWrappedBlock'
import PromptBtn from '../../../../../../buttons/PromptBtn/PromptBtn'
import { FormattedMessage } from 'react-intl'
import AppButton from '../../../../../../buttons/AppButton/AppButton'
import { ModelTypes } from '../../../../../types'
import { RuleBlockDictCard } from './RuleBlockDictCard'
import { BlockDictionaryTable } from '../../../../../BlockDictionary/components/BlockDictionaryTable'
import _cloneDeep from 'lodash/cloneDeep'
import {
    addRuleBlockDictItem,
    editRuleBlockDictValue,
    modifyRuleCardConfig,
} from '../../../../../../../store/actions/ruleCardActions'

export interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: any
    name: string
    title?: string
    wrapper?: string
    isViewOnly?: boolean
    element_id?: string
    inCard?: boolean
    isRuleActive?: boolean
}

export const RuleBlockDict = memo(function RuleBlockDict({
    dataPath,
    pathWithElementID,
    dataSchema = {},
    name,
    wrapper,
    isViewOnly,
    element_id,
    inCard,
    isRuleActive,
}: Props) {
    const { is_required: isRequired, is_empty_as_null: isEmptyAsNull } =
        dataSchema
    const dispatch = useAppDispatch()

    const path = useMemo(
        () => (dataPath?.length ? [...dataPath.split(PATH_JOINER), name] : []),
        [dataPath, name]
    )
    const errorKeyName = pathWithElementID ?? ''
    const configBit = useAppSelector(selectRuleConfigBit(path))

    const [isAddingNewDict, setIsAddingNewDict] = useState(false)
    const [dictKeys, setDictKeys] = useState(Object.keys(configBit || {}))
    const isCard = dataSchema.children?.value?.children

    const dataLen = Object.keys(configBit ?? {}).length

    const onAddHandle = () => {
        setIsAddingNewDict(true)
    }

    const handleAddDictValue = (
        dataItem: Record<string, string>,
        isEdit?: boolean
    ) => {
        if (isEdit) {
            const [[key, value]] = Object.entries(dataItem ?? {})
            dispatch(editRuleBlockDictValue({ path, key, value }))
        } else {
            dispatch(
                modifyRuleCardConfig({
                    path,
                    data: { [name]: _cloneDeep({ ...configBit, ...dataItem }) },
                })
            )
        }
    }

    const onAddCardDiction = (): void => {
        dispatch(
            addRuleBlockDictItem({
                path,
                data: configBit,
                item: { '': getEmptyData(dataSchema.children?.value) },
            })
        )
        if (dictKeys[dictKeys.length - 1] !== '') {
            setDictKeys([...dictKeys, ''])
        }
    }

    const footerData = getBlockFooterData(
        dictKeys,
        3,
        onAddCardDiction,
        'Add new diction'
    )

    useEffect(() => {
        if (isRequired && isEmptyAsNull && !dataLen) {
            dispatch(ruleCardActions.setRuleConfigInvalid({ errorKeyName }))
        }
        if (isRequired && isEmptyAsNull && dataLen) {
            dispatch(ruleCardActions.setRuleConfigValid({ errorKeyName }))
        }
    }, [isRequired, isEmptyAsNull, dataLen, errorKeyName, dispatch])

    if (isCard) {
        return (
            <RuleWrappedBlock
                key={name + path}
                dataPath={dataPath}
                inCard={inCard}
                isRuleActive={isRuleActive}
                element_id={element_id}
                isViewOnly={isViewOnly}
                title={dataSchema?.name || name}
                dataSchema={dataSchema}
                name={name}
                buttons={[
                    <>
                        {isRequired && isEmptyAsNull && !dataLen && (
                            <PromptBtn
                                className="me-2 mt-1"
                                type="validation"
                                prompt="At least 1 item required"
                            />
                        )}
                    </>,
                ]}
                elements={[
                    <div
                        className="d-flex flex-wrap"
                        id={'id'}
                    >
                        {dictKeys.length ? (
                            dictKeys.map((el: string) => (
                                <RuleBlockDictCard
                                    key={el}
                                    dataPath={path.join(PATH_JOINER)}
                                    title={name}
                                    dictionaryKey={el}
                                    value={configBit?.[el] ?? ''}
                                    data={configBit}
                                    dataSchema={dataSchema}
                                    name={el}
                                    wrapper={'block_dictionary_card_row'}
                                    setDictKeys={setDictKeys}
                                />
                            ))
                        ) : (
                            <span
                                style={{ opacity: '0.5' }}
                                className="h3 ms-auto me-auto text-secondary"
                            >
                                <FormattedMessage id="nodata" />
                            </span>
                        )}
                    </div>,
                ]}
                wrapper={'block_dictionary'}
                renderHeaderControl={(blockState?: boolean) => [
                    <AppButton
                        variant="add"
                        disabled={!blockState}
                        onClick={onAddCardDiction}
                        className="btn-success btn btn-add py-1 px-3 my-1 me-3 ms-auto"
                        tooltip={`Add new diction`}
                    />,
                ]}
                footerData={footerData}
                pathWithElementID={undefined}
            />
        )
    }

    return (
        <RuleWrappedBlock
            dataPath={dataPath}
            key={element_id ? element_id + name : path + name}
            inCard={inCard}
            isRuleActive={isRuleActive}
            element_id={element_id}
            isViewOnly={isViewOnly}
            title={dataSchema?.name}
            dataSchema={dataSchema}
            name={name}
            buttons={[
                <>
                    {isRequired && isEmptyAsNull && !dataLen && (
                        <PromptBtn
                            className="me-2 mt-1"
                            type="validation"
                            prompt="At least 1 item required"
                        />
                    )}
                </>,
            ]}
            renderHeaderControl={(blockState?: boolean) => [
                <AppButton
                    variant="add"
                    onClick={onAddHandle}
                    disabled={isAddingNewDict || !blockState}
                    className="btn btn-add py-1 px-3 my-1 me-3 ms-auto"
                    tooltip="tooltips.addNewRow"
                />,
            ]}
            elements={[
                <BlockDictionaryTable
                    dataPath={dataPath}
                    data={configBit}
                    dataSchema={dataSchema}
                    name={name}
                    path={path}
                    dictKeys={dictKeys}
                    setDictKeys={setDictKeys}
                    isAdding={isAddingNewDict}
                    setIsAdding={setIsAddingNewDict}
                    ruleCardAddDictValue={handleAddDictValue}
                />,
            ]}
            wrapper={wrapper || ModelTypes.BLOCK_DICTIONARY}
            pathWithElementID={undefined}
        />
    )
})
